import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { NavLink, useParams } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import * as R from 'ramda';
import { SwiftDispatchLogoWhite, SwiftLogoWhite } from 'library';
import { useUserRolePermissions } from 'hooks';
import { AuthContext } from 'authentication/AuthContext';
import { func } from 'prop-types';
import useCurrentSection from 'hooks/useCurrentSection';

const createMenuItemClickHandler = (onClick, item) => () => {
  if (item?.onClick?.apply) item.onClick(item.value || item.label);
  if (onClick?.apply) onClick();
};

// buildMenuItem :: Function -> Object -> MuiMenuItem
const buildMenuItem = R.curry((onClick, item) => (
  <MenuItem
    disabled={item.disabled}
    key={item.label}
    onClick={createMenuItemClickHandler(onClick, item)}
  >
    {item.element || item.label}
  </MenuItem>
));

const TopBar = ({ setIsNavDrawerOpen }) => {
  const theme = useTheme();
  const { userProfile } = useContext(AuthContext);
  const { currentAffiliateId, dataCompanyId } = useParams();

  // check for RBAC
  const { isAdmin, isUser } = useUserRolePermissions();
  const isProductionEnv = process.env.REACT_APP_BUILD_STATE === 'production';

  const menuAnchorRef = useRef(null);
  const [menuItems, setMenuItems] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDispatching, setIsDispatching] = useState(false);

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const baseRoute = `/affiliate/${currentAffiliateId}/${dataCompanyId}`;

  const userPermissions = {
    element: (
      <NavLink
        className="heap-button"
        onClick={() => setIsNavDrawerOpen(false)}
        style={{
          ...theme.typography.body1,
          textDecoration: 'none',
          width: '100%',
        }}
        to={`${baseRoute}/security`}
      >
        User Permissions
      </NavLink>
    ),
    label: 'User Permissions',
    onClick: R.always,
    value: 'User Permissions',
  };

  // eslint-disable-next-line no-unused-vars
  const subscriberPermissions = {
    element: (
      <NavLink
        className="heap-button"
        onClick={() => setIsNavDrawerOpen(false)}
        style={{
          ...theme.typography.body1,
          textDecoration: 'none',
          width: '100%',
        }}
        to={`${baseRoute}/swift-plus`}
      >
        Swift+
      </NavLink>
    ),
    label: 'Swift+',
    onClick: R.always,
    value: 'Swift+',
  };

  const currentSection = useCurrentSection();
  useEffect(() => {
    setIsDispatching(currentSection === 'dispatching');
  }, [currentSection]);

  useEffect(() => {
    isProductionEnv && isAdmin && setMenuItems([userPermissions]);
    if (!isProductionEnv) {
      if (isAdmin) {
        // ADO ticket 11448: Remove Swift+ (subscriberPermissions) from nav
        setMenuItems([userPermissions]);
      } else {
        // ADO ticket 11448: Remove Swift+ (subscriberPermissions) from nav
        setMenuItems([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProductionEnv, isAdmin]);
  return (
    <>
      <Box
        alignItems="center"
        bgcolor="secondary.dark"
        color="primary.contrastText"
        display="flex"
        height={48}
        justifyContent="space-between"
        px={2}
      >
        <IconButton
          className="heap-button"
          color="inherit"
          edge="start"
          onClick={() => setIsNavDrawerOpen(false)}
          size="large"
        >
          <CloseRoundedIcon />
        </IconButton>
        <NavLink style={{ display: 'block', width: '93px' }} to="">
          {isDispatching ? (
            <SwiftDispatchLogoWhite
              sx={{
                height: '100%',
                px: 0,
                py: 1,
                width: 'auto',
              }}
            />
          ) : (
            <SwiftLogoWhite
              sx={{
                height: '100%',
                px: 0,
                py: 1,
                width: 'auto',
              }}
            />
          )}
        </NavLink>
      </Box>
      <Box
        alignItems="center"
        bgcolor="primary.dark"
        color="surface.white"
        display="flex"
        pl={2}
        pr={1}
        py={2}
      >
        <AccountCircleRoundedIcon color="inherit" />
        <Typography
          color="inherit"
          sx={{
            mb: 0,
            ml: 2,
            mr: 0,
            mt: '2px',
          }}
        >
          {userProfile.given_name} {userProfile.family_name}
        </Typography>
        {/* if an established user and has security admin permissions and/or subscriber manage */}
        {isUser && menuItems.length > 0 && (
          <>
            <IconButton
              className="heap-button"
              data-testid="settingsIcon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              size="medium"
              sx={{
                color: 'surface.white',
                height: 24,
                marginLeft: 'auto',
                padding: 1.5,
              }}
            >
              <SettingsIcon ref={menuAnchorRef} />
            </IconButton>
            <Menu
              anchorEl={menuAnchorRef?.current}
              anchorOrigin={{
                horizontal: -154,
                vertical: 'bottom',
              }}
              onClose={closeMenu}
              open={isMenuOpen}
              sx={{ marginLeft: 'auto' }}
            >
              {R.map(buildMenuItem(closeMenu), menuItems)}
            </Menu>
          </>
        )}
      </Box>
    </>
  );
};

TopBar.propTypes = {
  setIsNavDrawerOpen: func.isRequired,
};

export default TopBar;
